'use client';

import type { OrderUnreadSummary } from '@lib/data/order-consultations';
import { useUnread } from '@modules/account/components/unread-provider';
import { usePathname } from 'next/navigation';
import { useCallback, useEffect } from 'react';

// Refreshes the unread badge on client-side navigation and when the tab
// becomes visible again. Mount only for logged-in users (controlled by the
// parent). Fetches on mount so the badge appears immediately after login
// (the component mounts when customer transitions from null → non-null).
export default function UnreadSummaryNavRefresh() {
  const pathname = usePathname();
  const { rehydrate } = useUnread();

  const fetchSummary = useCallback(() => {
    if (document.hidden) {
      return;
    }
    const controller = new AbortController();
    fetch('/api/order-messaging/unread-summary', {
      signal: controller.signal,
      cache: 'no-store',
    })
      .then((res) => {
        if (!res.ok) {
          return;
        }
        return res.json() as Promise<OrderUnreadSummary>;
      })
      .then((summary) => {
        if (summary) {
          rehydrate(summary);
        }
      })
      .catch(() => undefined);
    return controller;
  }, [rehydrate]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: pathname is an intentional trigger — the effect re-runs on navigation but doesn't reference pathname in its body
  useEffect(() => {
    const controller = fetchSummary();
    return () => controller?.abort();
  }, [pathname, fetchSummary]);

  useEffect(() => {
    let controller: AbortController | undefined;

    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        controller?.abort();
        controller = fetchSummary();
      }
    };

    document.addEventListener('visibilitychange', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      controller?.abort();
    };
  }, [fetchSummary]);

  return null;
}
